import React, { useState, useEffect } from 'react';
import {
  RegulatoryAuthority,
  RegulatoryFramework,
  RegulatoryRequirement,
  RegulatoryDashboardStats,
} from './types';
import { regulatoryService } from './regulatoryService';
import { RegulatoryFrameworksList } from './components/RegulatoryFrameworksList';
import { RequirementsCatalogTable } from './components/RequirementsCatalogTable';
import { RequirementDetailModal } from './components/RequirementDetailModal';
import { RegulatoryChangesTab } from './components/RegulatoryChangesTab';
import { ComplianceCoverageTab } from './components/ComplianceCoverageTab';

export const RegulatoryFrameworksPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'FRAMEWORKS' | 'REQUIREMENTS' | 'CHANGES' | 'COVERAGE'>('FRAMEWORKS');

  const [stats, setStats] = useState<RegulatoryDashboardStats | null>(null);
  const [authorities, setAuthorities] = useState<RegulatoryAuthority[]>([]);
  const [frameworks, setFrameworks] = useState<RegulatoryFramework[]>([]);
  const [requirements, setRequirements] = useState<RegulatoryRequirement[]>([]);

  // Filters
  const [selectedAuthority, setSelectedAuthority] = useState<string>('');
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Modal
  const [selectedRequirement, setSelectedRequirement] = useState<RegulatoryRequirement | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadRequirements();
  }, [selectedFrameworkId, searchQuery, selectedType]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [statsData, authData, frmData] = await Promise.all([
        regulatoryService.getDashboardStats(),
        regulatoryService.getAuthorities(),
        regulatoryService.getFrameworks(),
      ]);
      setStats(statsData);
      setAuthorities(authData);
      setFrameworks(frmData);
    } catch (err) {
      console.error('Error loading regulatory initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async () => {
    try {
      setLoading(true);
      const reqsData = await regulatoryService.getRequirements({
        frameworkId: selectedFrameworkId || undefined,
        query: searchQuery || undefined,
        requirementType: selectedType || undefined,
      });
      setRequirements(reqsData);
    } catch (err) {
      console.error('Error loading requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFrameworkCard = (frameworkId: string) => {
    setSelectedFrameworkId(frameworkId);
    setActiveTab('REQUIREMENTS');
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="page-header-banner">
        <div className="badge">REGULATORY & COMPLIANCE</div>
        <h1>Regulatory Frameworks & Oversight</h1>
        <p>EASA, ICAO, SHGM, FAA and IOSA regulatory library: Canonical hierarchy, immutable versioning, applicability engines, and delta tracking.</p>
      </div>

      {/* KPI Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs">
            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Authorities</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.totalAuthorities}</div>
            <div className="text-xs text-gray-400 mt-0.5">EASA, ICAO, SHGM...</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-200 bg-blue-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-blue-800 uppercase tracking-wider">Frameworks</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{stats.totalFrameworks}</div>
            <div className="text-xs text-blue-500 mt-0.5">Air Ops, SMS, ISM</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-indigo-200 bg-indigo-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-indigo-800 uppercase tracking-wider">Documents</div>
            <div className="text-2xl font-bold text-indigo-700 mt-1">{stats.totalDocuments}</div>
            <div className="text-xs text-indigo-500 mt-0.5">Regulations / Directives</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-slate-200 bg-slate-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">Requirements</div>
            <div className="text-2xl font-bold text-slate-700 mt-1">{stats.totalRequirements}</div>
            <div className="text-xs text-slate-500 mt-0.5">Normative Rules</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-teal-200 bg-teal-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-teal-800 uppercase tracking-wider">Applicable</div>
            <div className="text-2xl font-bold text-teal-700 mt-1">{stats.totalApplicableRequirements}</div>
            <div className="text-xs text-teal-600 mt-0.5">Under AOC Scope</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-emerald-200 bg-emerald-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">Mapped Controls</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.mappedToControlsCount}</div>
            <div className="text-xs text-emerald-600 mt-0.5">SOPs / Manuals</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-green-200 bg-green-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-green-800 uppercase tracking-wider">Coverage Rate</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{stats.complianceCoveragePercentage}%</div>
            <div className="text-xs text-green-600 mt-0.5">Assurance Ratio</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-amber-200 bg-amber-50/20 shadow-xs">
            <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">Active Deltas</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{stats.openChangesCount}</div>
            <div className="text-xs text-amber-600 mt-0.5">Under Assessment</div>
          </div>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg px-4 flex space-x-6 text-xs font-bold">
        <button
          onClick={() => setActiveTab('FRAMEWORKS')}
          className={`py-3.5 border-b-2 transition-colors ${
            activeTab === 'FRAMEWORKS'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Regulatory Frameworks & Authorities ({frameworks.length})
        </button>

        <button
          onClick={() => setActiveTab('REQUIREMENTS')}
          className={`py-3.5 border-b-2 transition-colors ${
            activeTab === 'REQUIREMENTS'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Requirement Articles Catalog ({requirements.length})
        </button>

        <button
          onClick={() => setActiveTab('CHANGES')}
          className={`py-3.5 border-b-2 transition-colors ${
            activeTab === 'CHANGES'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Amendments & Delta Impact Analysis
        </button>

        <button
          onClick={() => setActiveTab('COVERAGE')}
          className={`py-3.5 border-b-2 transition-colors ${
            activeTab === 'COVERAGE'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Compliance Matrix & Gap Analysis
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === 'FRAMEWORKS' && (
        <RegulatoryFrameworksList
          frameworks={frameworks}
          authorities={authorities}
          selectedAuthority={selectedAuthority}
          onSelectAuthority={setSelectedAuthority}
          onSelectFramework={handleSelectFrameworkCard}
        />
      )}

      {activeTab === 'REQUIREMENTS' && (
        <RequirementsCatalogTable
          requirements={requirements}
          frameworks={frameworks}
          selectedFrameworkId={selectedFrameworkId}
          searchQuery={searchQuery}
          selectedType={selectedType}
          loading={loading}
          onFrameworkChange={setSelectedFrameworkId}
          onSearchChange={setSearchQuery}
          onTypeChange={setSelectedType}
          onSelectRequirement={(req) => setSelectedRequirement(req)}
        />
      )}

      {activeTab === 'CHANGES' && (
        <RegulatoryChangesTab
          onRefresh={() => {
            loadInitialData();
            loadRequirements();
          }}
        />
      )}

      {activeTab === 'COVERAGE' && (
        <ComplianceCoverageTab
          stats={stats}
          requirements={requirements}
          onSelectRequirement={(req) => setSelectedRequirement(req)}
        />
      )}

      {/* Requirement Detail Modal */}
      {selectedRequirement && (
        <RequirementDetailModal
          requirement={selectedRequirement}
          onClose={() => setSelectedRequirement(null)}
          onRefresh={() => {
            loadInitialData();
            loadRequirements();
          }}
        />
      )}
    </div>
  );
};
